/**
 * Mapbox Style Safety Utilities
 *
 * Provides consistent patterns for safely interacting with Mapbox styles
 * to prevent "Style is not done loading" errors during style transitions.
 */

import type { Map } from 'mapbox-gl';

/**
 * Check if map style is fully loaded and ready for operations.
 * Use this before any getStyle(), getLayer(), getSource(), or queryRenderedFeatures() calls.
 *
 * @param map - Mapbox map instance (can be null)
 * @returns true if style is loaded and safe to use
 */
export function isStyleReady(map: Map | null): boolean {
  if (!map) return false;

  try {
    // isStyleLoaded() must return true AND getStyle() must return non-null
    return map.isStyleLoaded() && map.getStyle() !== undefined;
  } catch {
    // If any error occurs (e.g., map is being destroyed), not ready
    return false;
  }
}

/**
 * Read the current style's name without throwing.
 * getStyle() internally serializes the style and throws
 * "Style is not done loading" during style transitions — optional chaining
 * on the result cannot guard against that.
 *
 * @param map - Mapbox map instance (can be null)
 * @returns The style name, or null when unavailable
 */
export function getStyleName(map: Map | null): string | null {
  if (!map) return null;

  try {
    return map.getStyle()?.name ?? null;
  } catch {
    return null;
  }
}

/**
 * Check if a specific layer exists and is safe to interact with.
 * Combines style readiness check with layer existence check.
 *
 * @param map - Mapbox map instance
 * @param layerId - ID of the layer to check
 * @returns true if layer exists and is safe to use
 */
export function isLayerReady(map: Map | null, layerId: string): boolean {
  if (!isStyleReady(map)) return false;

  try {
    return map!.getLayer(layerId) !== undefined;
  } catch {
    return false;
  }
}

/**
 * Filter array of layer IDs to only those that exist and are ready.
 * Safe to call during style transitions.
 *
 * @param map - Mapbox map instance
 * @param layerIds - Array of layer IDs to check
 * @returns Array of layer IDs that exist and are ready
 */
export function getReadyLayers(map: Map | null, layerIds: string[]): string[] {
  if (!isStyleReady(map)) return [];

  return layerIds.filter(layerId => {
    try {
      return map!.getLayer(layerId) !== undefined;
    } catch {
      return false;
    }
  });
}
